import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ManagerKpi } from './types'

function formatRub(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${Math.round(n)}`
}

function Delta({ value, suffix = '%' }: { value: number | null; suffix?: string }) {
  if (value === null || value === 0) return null
  const up = value > 0
  const cls = up ? 'text-emerald-600' : 'text-red-600'
  return (
    <span className={`text-xs font-medium ${cls}`}>
      {up ? '↑' : '↓'} {Math.abs(value)}
      {suffix}
    </span>
  )
}

interface CardProps {
  label: string
  value: ReactNode
  tone?: 'red' | 'amber' | 'blue' | 'coral' | 'neutral' | 'emerald'
  hint?: ReactNode
  to?: string
}

function Card({ label, value, tone = 'neutral', hint, to }: CardProps) {
  const bg = {
    red: 'bg-red-50/60 border-red-100',
    amber: 'bg-amber-50/60 border-amber-100',
    blue: 'bg-blue-50/60 border-blue-100',
    coral: 'bg-orange-50/60 border-orange-100',
    neutral: 'bg-gray-50 border-gray-200',
    emerald: 'bg-emerald-50/60 border-emerald-100',
  }[tone]

  const inner = (
    <div className={`rounded-2xl border ${bg} p-4 h-full flex flex-col justify-between`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-3xl font-bold text-gray-900 leading-tight mt-2">{value}</div>
      {hint && <div className="mt-1">{hint}</div>}
    </div>
  )
  return to ? (
    <Link to={to} className="block hover:shadow-sm transition-shadow">
      {inner}
    </Link>
  ) : (
    inner
  )
}

export function ManagerKpiCards({ kpi }: { kpi: ManagerKpi }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <Card
        label="Горящих дедлайнов"
        value={kpi.burningDeadlines.count}
        tone="red"
        hint={
          kpi.burningDeadlines.overdue > 0 ? (
            <span className="text-xs text-red-600">{kpi.burningDeadlines.overdue} просрочены</span>
          ) : null
        }
        to="/requests?filter=burning"
      />
      <Card
        label="Ждут меня"
        value={kpi.waitingForMe.count}
        tone="amber"
        hint={<span className="text-xs text-amber-700">готовы к КП</span>}
        to="/requests?status=QUOTATION_READY"
      />
      <Card
        label="Комментарии"
        value={kpi.unreadComments.count}
        tone="blue"
        hint={<span className="text-xs text-blue-700">за последние 24 ч</span>}
      />
      <Card
        label="Без движения > 3 дн."
        value={kpi.staleItems.count}
        tone="coral"
        hint={<span className="text-xs text-orange-700">закупщик молчит</span>}
      />
      <Card
        label="Активных заявок"
        value={kpi.activeRequests.count}
        tone="neutral"
        hint={<Delta value={kpi.activeRequests.deltaCount} suffix="" />}
        to="/requests?filter=active"
      />
      <Card
        label="В заказе"
        value={formatRub(kpi.kpSum.currentRub)}
        tone="emerald"
        hint={<Delta value={kpi.kpSum.deltaPercent} />}
      />
    </div>
  )
}
